import React from "react";
import { X, Briefcase } from "lucide-react";

interface UserDetailPopupProps {
  user: {
    name: string;
    role: string;
    email: string;
    profile_image?: string | null;
    avatar?: string;
    workspace_name?: string;
    total_cards_created?: number;
    total_feedback?: number;
    total_screenshots?: number;
    total_recordings?: number;
    last_active?: string;
  } | null;
  isOpen: boolean;
  onClose: () => void;
  loading?: boolean;
}

// Skeleton loader component
const SkeletonLoader: React.FC<{ className?: string }> = ({ className = "" }) => (
  <div className={`animate-pulse bg-gray-200 rounded ${className}`}></div>
);

const UserDetailPopup: React.FC<UserDetailPopupProps> = ({
  user,
  isOpen,
  onClose,
  loading = false,
}) => {
  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-30 z-40 transition-opacity"
        onClick={onClose}
      />

      {/* Popup */}
      <div className="fixed right-0 top-0 h-full w-full sm:w-[312px] 3xl:w-[400px] bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out flex flex-col">
        {user ? (
          // User Data
          <>
            <div className="flex-1 overflow-y-auto p-3">
              {/* Header with profile image */}
              <div className="relative mb-6">
                <div className="w-full h-64 bg-gradient-to-br from-orange-200 to-orange-300 rounded-lg overflow-hidden flex items-center justify-center">
                  {user.profile_image ? (
                    <img
                      src={user.profile_image}
                      alt={user.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        // Fallback to gradient background with emoji/initials
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  ) : (
                    <div className="text-7xl">
                      {user.avatar || user.name.charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>
              </div>

              {/* User Info */}
              <div className="mb-3">
                <h2 className="text-2xl font-semibold text-gray-900 mb-1">
                  {user.name}
                </h2>
                <div className="flex items-center justify-between text-gray-600 mb-4">
                  <div>
                    <span className="text-sm">{user.role} @</span>
                    {loading || !user.workspace_name ? (
                      <SkeletonLoader className="inline-block w-20 h-4 ml-1" />
                    ) : (
                      <span className="text-sm font-medium">
                        {user.workspace_name}
                      </span>
                    )}
                  </div>
                  <Briefcase className="w-4 h-4 ml-1" />
                </div>
              </div>
              <div className="border-t border-gray-200 mb-4"></div>

              {/* Other Details Section */}
              <div className="mb-2">
                <h3 className="text-[14px] font-medium font-inter text-gray-900 mb-2">
                  Other details
                </h3>

                <div className="space-y-3">
                  <div className="bg-[#F7F9FB] border-[0.5px] border-[#D3D3D3] rounded-lg p-[10px] space-y-1">
                    <div className="flex justify-between items-center">
                      <span className="text-[#223345] text-[12px]">
                        Total project created :
                      </span>
                      {loading || user.total_cards_created === undefined ? (
                        <SkeletonLoader className="w-12 h-5" />
                      ) : (
                        <span className="text-[#050505] font-bold text-[14px]">
                          {user.total_cards_created}
                        </span>
                      )}
                    </div>
                    <div className="border-t border-gray-200 mb-1"></div>
                    <div className="flex justify-between items-center">
                      <span className="text-[#223345] text-[12px]">
                        Total feedback created :
                      </span>
                      {loading || user.total_feedback === undefined ? (
                        <SkeletonLoader className="w-12 h-5" />
                      ) : (
                        <span className="text-[#050505] font-bold text-[14px]">
                          {user.total_feedback}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="bg-[#F7F9FB] border-[0.5px] border-[#D3D3D3] rounded-lg p-[10px] space-y-1">
                    <div className="flex justify-between items-center">
                      <span className="text-[#223345] text-[12px]">
                        Screenshots :
                      </span>
                      {loading || user.total_screenshots === undefined ? (
                        <SkeletonLoader className="w-12 h-5" />
                      ) : (
                        <span className="text-[#050505] font-bold text-[14px]">
                          {user.total_screenshots}
                        </span>
                      )}
                    </div>
                    <div className="border-t border-gray-200 mb-1"></div>
                    <div className="flex justify-between items-center">
                      <span className="text-[#223345] text-[12px]">
                        Recordings :
                      </span>
                      {loading || user.total_recordings === undefined ? (
                        <SkeletonLoader className="w-12 h-5" />
                      ) : (
                        <span className="text-[#050505] font-bold text-[14px]">
                          {user.total_recordings}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Last Active */}
              <div className="mb-6">
                <div className="flex items-center gap-2 py-2">
                  <span className="text-[#223345] text-[14px] font-inter">
                    Last active:{" "}
                  </span>
                  {loading || !user.last_active ? (
                    <SkeletonLoader className="w-28 h-5" />
                  ) : (
                    <span className="text-[#050505] text-[14px] font-inter font-medium">
                      {user.last_active}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Separator and Close Button - Fixed at bottom */}
            <div className="border-t border-gray-200 mx-3">
              <div className="py-3">
                <button
                  onClick={onClose}
                  className="w-full py-3 border border-[#21262C] rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </>
        ) : (
          // No User Data
          <div className="flex-1 flex items-center justify-center">
            <p className="text-gray-500">No user data available</p>
          </div>
        )}
      </div>
    </>
  );
};

export default UserDetailPopup;